import { useMemo } from "react";
import type { CollectionDto } from "../api";
import {
  addArticleToCollection,
  type BookmarkedArticles,
  CACHE_KEYS,
  copyCollection,
  createCollection,
  deleteCollection,
  followAuthor,
  getBookmarkedArticles,
  getCollections,
  getFollowing,
  getReadArticleKeys,
  markArticleRead,
  markArticleUnread,
  removeArticleFromCollection,
  renameCollection,
  unfollowAuthor,
} from "../client";
import { toArticleKey } from "../collections";
import { useCacheValue } from "./useCacheValue";

const FOLLOWING_KEYS = [CACHE_KEYS.following];
const READ_KEYS = [CACHE_KEYS.articlesRead];
const COLLECTION_KEYS = [CACHE_KEYS.collections];
const BOOKMARK_KEYS = [CACHE_KEYS.bookmarked];
const NONE: string[] = [];
const NO_BOOKMARKS: BookmarkedArticles = {};
const NO_COLLECTIONS: CollectionDto[] = [];

export type UseFollowingResult = {
  following: Set<string>;
  isLoading: boolean;
  isFollowing: (alias: string) => boolean;
  follow: (alias: string) => Promise<void>;
  unfollow: (alias: string) => Promise<void>;
};

export const useFollowing = (): UseFollowingResult => {
  const { value, isLoading } = useCacheValue(getFollowing, FOLLOWING_KEYS, NONE);
  const following = useMemo(() => new Set(value), [value]);
  return { following, isLoading, isFollowing: (alias) => following.has(alias), follow: followAuthor, unfollow: unfollowAuthor };
};

export type UseReadArticlesResult = {
  readKeys: Set<string>;
  isLoading: boolean;
  isRead: (alias: string, articleId: string) => boolean;
  markRead: (alias: string, articleId: string) => Promise<void>;
  markUnread: (alias: string, articleId: string) => Promise<void>;
};

export const useReadArticles = (): UseReadArticlesResult => {
  const { value, isLoading } = useCacheValue(getReadArticleKeys, READ_KEYS, NONE);
  const readKeys = useMemo(() => new Set(value), [value]);
  return {
    readKeys,
    isLoading,
    isRead: (alias, articleId) => readKeys.has(toArticleKey(alias, articleId)),
    markRead: markArticleRead,
    markUnread: markArticleUnread,
  };
};

export type UseCollectionsResult = {
  collections: CollectionDto[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (name: string) => Promise<CollectionDto>;
  rename: (id: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  copy: (id: string, targetCollectionId: string) => Promise<boolean>;
};

export const useCollections = (): UseCollectionsResult => {
  const { value, isLoading, error, refresh } = useCacheValue(getCollections, COLLECTION_KEYS, NO_COLLECTIONS);
  return { collections: value, isLoading, error, refresh, create: createCollection, rename: renameCollection, remove: deleteCollection, copy: copyCollection };
};

export type UseBookmarksResult = {
  bookmarked: BookmarkedArticles;
  isLoading: boolean;
  isBookmarked: (alias: string, articleId: string) => boolean;
  collectionIdsOf: (alias: string, articleId: string) => string[];
  add: (collectionId: string, alias: string, articleId: string) => Promise<boolean>;
  remove: (collectionId: string, alias: string, articleId: string) => Promise<void>;
};

export const useBookmarks = (): UseBookmarksResult => {
  const { value, isLoading } = useCacheValue(getBookmarkedArticles, BOOKMARK_KEYS, NO_BOOKMARKS);
  const collectionIdsOf = (alias: string, articleId: string): string[] => value[toArticleKey(alias, articleId)] ?? NONE;
  return {
    bookmarked: value,
    isLoading,
    isBookmarked: (alias, articleId) => collectionIdsOf(alias, articleId).length > 0,
    collectionIdsOf,
    add: addArticleToCollection,
    remove: removeArticleFromCollection,
  };
};
